import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import toast from 'react-hot-toast'

const AuthContext = createContext({})
const PROFILE_CACHE_KEY = 'auth_profile_cache'

function isTimeoutError(error) {
  return error instanceof Error && error.message?.includes('Timed out')
}

function getCachedProfile() {
  try {
    const rawValue = window.localStorage.getItem(PROFILE_CACHE_KEY)
    return rawValue ? JSON.parse(rawValue) : null
  } catch {
    return null
  }
}

function cacheProfile(profile) {
  try {
    if (profile) {
      window.localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile))
    } else {
      window.localStorage.removeItem(PROFILE_CACHE_KEY)
    }
  } catch {
    // Ignore cache write failures and continue with in-memory state.
  }
}

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(() => getCachedProfile())
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  function withTimeout(promise, timeoutMs, message) {
    let timeoutId

    return Promise.race([
      promise,
      new Promise((_, reject) => {
        timeoutId = window.setTimeout(() => reject(new Error(message)), timeoutMs)
      }),
    ]).finally(() => {
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }
    })
  }

  const fetchProfile = useCallback(async (userId) => {
    setProfileLoading(true)

    try {
      const { data, error } = await withTimeout(
        supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single(),
        10000,
        'Timed out while loading your profile.'
      )

      if (error) throw error
      setProfile(data)
      cacheProfile(data)
    } catch (err) {
      const cached = getCachedProfile()

      if (cached?.id === userId) {
        setProfile(cached)
      }

      if (isTimeoutError(err)) {
        return
      }

      console.error('Profile fetch error:', err)

      // Only show toast for non-timeout errors when there's no cached profile
      if (!cached || cached.id !== userId) {
        toast.error(err.message || 'Failed to load profile')
      }
    } finally {
      setProfileLoading(false)
    }
  }, [])

  useEffect(() => {
    let isMounted = true

    const initializeAuth = async () => {
      try {
        const {
          data: { session },
          error,
        } = await withTimeout(
          supabase.auth.getSession(),
          8000,
          'Timed out while restoring your session.'
        )

        if (error) throw error
        if (!isMounted) return

        setUser(session?.user ?? null)
        setLoading(false)

        if (session?.user) {
          const cachedProfile = getCachedProfile()

          if (cachedProfile?.id === session.user.id) {
            setProfile(cachedProfile)
          } else {
            setProfile(null)
            cacheProfile(null)
          }

          await fetchProfile(session.user.id)
        } else {
          setProfile(null)
          cacheProfile(null)
        }
      } catch (err) {
        if (!isMounted) return

        if (isTimeoutError(err)) {
          setLoading(false)
          setProfileLoading(false)
          return
        }

        console.error('Error restoring auth session:', err)
        setUser(null)
        setProfile(null)
        setLoading(false)
        setProfileLoading(false)
        cacheProfile(null)
      }
    }

    initializeAuth()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (!isMounted) return
        setUser(session?.user ?? null)
        setLoading(false)

        if (session?.user) {
          const cachedProfile = getCachedProfile()

          if (cachedProfile?.id === session.user.id) {
            setProfile(cachedProfile)
          } else {
            setProfile(null)
            cacheProfile(null)
          }

          await fetchProfile(session.user.id)
        } else {
          setProfile(null)
          setProfileLoading(false)
          cacheProfile(null)
        }
      }
    )

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [fetchProfile])

  async function signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) throw error
    return data
  }

  async function sendPasswordResetEmail(email) {
    const redirectTo = `${window.location.origin}/reset-password`
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    })

    if (error) throw error
    return data
  }

  async function updatePassword(password) {
    const { data, error } = await supabase.auth.updateUser({
      password,
    })

    if (error) throw error
    return data
  }

  async function signOut() {
    setUser(null)
    setProfile(null)
    setLoading(false)
    setProfileLoading(false)
    cacheProfile(null)

    const { error } = await supabase.auth.signOut({ scope: 'local' })
    if (error) throw error
  }

  // Helper to get role name from joined roles table
  const roleName = profile?.role_id || profile?.roles?.id || null

  const value = {
    user,
    profile,
    loading,
    profileLoading,
    signIn,
    signOut,
    sendPasswordResetEmail,
    updatePassword,
    roleName,
    isAdmin: profile?.role_id === 'super_admin' || profile?.role_id === 'admin',
    isSuperAdmin: profile?.role_id === 'super_admin',
    getDisplayName: () => {
      if (profile?.first_name || profile?.last_name) {
        return `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
      }
      return profile?.email || user?.email || 'Admin'
    },
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
